import React, { useState, useEffect, useCallback } from "react";
import "./JunkMail.css";
import { Calendar, SlidersHorizontal, Search, ChevronDown, ChevronLeft, ChevronRight, Check, X, ArrowLeft, Download, FileText, User } from "lucide-react";
import { Spinner, Toast, ToastContainer, Modal, Button } from "react-bootstrap";
import JunkMailView from "./JunkMailView";
import { apiCall } from "../../services/api-service";

import excelIcon from "../../Assets/png/excel.png";
import docsIcon from "../../Assets/png/docs.png";
import fileIcon from "../../Assets/png/file.png";
import imageIcon from "../../Assets/png/image.png";

const formatDateTime = (dateStr) => {
  if (!dateStr) return "-";
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr;
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }) + " at " + date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    }).replace(" ", "");
  } catch {
    return dateStr;
  }
};

const getInitials = (name, email) => {
  const str = name || email || "JM";
  const parts = str.split(/[\s._@-]+/).filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return str.substring(0, 2).toUpperCase();
};

const renderStatusBadge = (statusStr) => {
  const status = (statusStr || "").toLowerCase();
  if (status.includes("user") || status.includes("create_user")) {
    return <span className="status-pill pill-user-ticket">User & Ticket Created</span>;
  } else if (status.includes("ticket") || status.includes("create_ticket") || status.includes("approved") || status.includes("approve")) {
    return <span className="status-pill pill-ticket-created">Ticket Created</span>;
  } else {
    return <span className="status-pill pill-rejected">Rejected</span>;
  }
};

const isImageFile = (ext) => ["JPG", "JPEG", "PNG", "GIF", "WEBP", "SVG"].includes(ext);
const isExcelFile = (ext) => ["XLS", "XLSX", "CSV"].includes(ext);
const isWordFile = (ext) => ["DOC", "DOCX"].includes(ext);
const isPdfFile = (ext) => ext === "PDF";

const renderAttachmentIcon = (fileName, fileUrl) => {
  const ext = (fileName.split(".").pop() || "").toUpperCase();

  if (isImageFile(ext)) {
    return (
      <img
        src={fileUrl}
        alt={fileName}
        className="attachment-img-preview"
        onError={(e) => {
          e.target.onerror = null;
          e.target.src = imageIcon;
        }}
      />
    );
  } else if (isExcelFile(ext)) {
    return <img src={excelIcon} alt="Excel" className="attachment-icon-img" />;
  } else if (isWordFile(ext)) {
    return <img src={docsIcon} alt="Word" className="attachment-icon-img" />;
  } else if (isPdfFile(ext)) {
    return (
      <div className="attachment-type-badge">
        PDF
      </div>
    );
  } else {
    return <img src={fileIcon} alt="File" className="attachment-icon-img" />;
  }
};

const JunkMailList = () => {
  const [activeTab, setActiveTab] = useState("junk");
  const [searchQuery, setSearchQuery] = useState("");
  const [recordsPerPage, setRecordsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  // API State
  const [junkMails, setJunkMails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  // View Page State
  const [selectedMailId, setSelectedMailId] = useState(null);
  const [viewMailData, setViewMailData] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);
  const [activeActionType, setActiveActionType] = useState(null);

  // Action Comments Modal State
  const [showActionModal, setShowActionModal] = useState(false);
  const [actionTarget, setActionTarget] = useState(null);
  const [actionReason, setActionReason] = useState("");

  const triggerActionModal = (action, isBulk = false) => {
    setActionTarget({ action, isBulk });
    setActionReason("");
    setShowActionModal(true);
  };

  const handleConfirmAction = () => {
    if (!actionTarget) return;
    if (actionTarget.isBulk) {
      handleBulkAction(actionTarget.action, actionReason);
    } else {
      handleAction(actionTarget.action, actionReason);
    }
  };

  // Toast Notification State (Project Standard)
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarStatus, setSnackbarStatus] = useState(false);
  const handleSnackbarShow = () => setShowSnackbar(true);
  const handleSnackbarClose = () => setShowSnackbar(false);

  const triggerToast = (msg, status = true) => {
    let messageStr = "Something went wrong";
    if (typeof msg === "string") {
      messageStr = msg;
    } else if (msg && typeof msg === "object") {
      messageStr = msg.message || JSON.stringify(msg);
    }
    setSnackbarMessage(messageStr);
    setSnackbarStatus(Boolean(status));
    setShowSnackbar(true);
  };

  // Fetch List API Data
  const fetchJunkMailData = useCallback(async () => {
    setLoading(true);
    try {
      const skip = (currentPage - 1) * recordsPerPage;

      const response = await apiCall("get", "junk_mail/pending_approvals", null, {
        params: {
          search: searchQuery || undefined,
          skip: skip,
          limit: recordsPerPage,
          type: activeTab,
        },
      });

      if (response?.status === 200 && response?.data?.success) {
        const records = response.data.junk_mails || [];
        const total = response.data.total_rec || 0;
        const pages = response.data.total_pgs || 1;

        setJunkMails(Array.isArray(records) ? records : []);
        setTotalRecords(total);
        setTotalPages(pages);
      } else {
        setJunkMails([]);
        setTotalRecords(0);
        setTotalPages(1);
      }
    } catch (error) {
      console.error("Error fetching junk mail list:", error);
      setJunkMails([]);
      setTotalRecords(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [searchQuery, currentPage, recordsPerPage, activeTab]);

  useEffect(() => {
    fetchJunkMailData();
  }, [fetchJunkMailData]);

  // Fetch single junk mail detail for Full View Page
  const handleViewMail = async (id) => {
    setSelectedMailId(id);
    setViewLoading(true);
    setViewMailData(null);
    try {
      const response = await apiCall("get", `junk_mail/view/${id}`);
      if (response?.status === 200 && response?.data?.success) {
        setViewMailData(response.data.data);
      } else {
        setViewMailData(null);
      }
    } catch (error) {
      console.error("Error viewing junk mail:", error);
      setViewMailData(null);
    } finally {
      setViewLoading(false);
    }
  };

  // Perform Approval / Rejection Action for Single Mail on View Page
  const handleAction = async (action, reason = "") => {
    if (!selectedMailId) return;
    setActiveActionType(action);
    try {
      const response = await apiCall("post", "junk_mail/action", {
        id: selectedMailId,
        action: action,
        reason: reason,
      });

      if (response?.status === 200 && response?.data?.success) {
        triggerToast(response.data.message || "Action completed successfully!", true);
        setSelectedMailId(null);
        setViewMailData(null);
        fetchJunkMailData();
      } else {
        const errorMsg =
          response?.data?.message ||
          response?.data?.error ||
          "Action failed. Please try again.";
        triggerToast(errorMsg, false);
      }
    } catch (error) {
      console.error("Error performing action on junk mail:", error);
      triggerToast("Error processing action.", false);
    } finally {
      setActiveActionType(null);
      setShowActionModal(false);
    }
  };

  // Bulk action for selected rows
  const handleBulkAction = async (action, reason = "") => {
    if (selectedRows.length === 0) return;
    setActiveActionType(action);
    try {
      const response = await apiCall("post", "junk_mail/action", {
        id: selectedRows,
        action: action,
        reason: reason,
      });

      if (response?.status === 200 && response?.data?.success) {
        triggerToast(response.data.message || "Bulk action completed successfully!", true);
        setSelectedRows([]);
        setSelectAll(false);
        fetchJunkMailData();
      } else {
        const errorMsg =
          response?.data?.message ||
          response?.data?.error ||
          "Bulk action failed.";
        triggerToast(errorMsg, false);
      }
    } catch (error) {
      console.error("Error performing bulk action:", error);
      triggerToast("Error performing bulk action.", false);
    } finally {
      setActiveActionType(null);
      setShowActionModal(false);
    }
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedRows([]);
    } else {
      setSelectedRows(junkMails.map((m) => m.id || m._id));
    }
    setSelectAll(!selectAll);
  };

  const handleSelectRow = (id) => {
    if (selectedRows.includes(id)) {
      setSelectedRows(selectedRows.filter((rowId) => rowId !== id));
    } else {
      setSelectedRows([...selectedRows, id]);
    }
  };

  const handleDownloadAttachment = async (e, fileUrl, fileName) => {
    e.preventDefault();
    if (!fileUrl || fileUrl === "#") return;

    try {
      const response = await fetch(fileUrl);
      if (!response.ok) {
        window.open(fileUrl, "_blank");
        return;
      }
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.style.display = "none";
      a.href = blobUrl;
      a.download = fileName || "attachment";
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(blobUrl);
      document.body.removeChild(a);
    } catch (err) {
      console.error("Direct fetch failed, opening URL in new tab:", err);
      window.open(fileUrl, "_blank");
    }
  };

  // If a mail is clicked, render Full Page Detail View using separate JunkMailView component
  if (selectedMailId !== null) {
    return (
      <JunkMailView
        selectedMailId={selectedMailId}
        onBack={() => {
          setSelectedMailId(null);
          fetchJunkMailData();
        }}
        activeTab={activeTab}
        onActionSuccess={fetchJunkMailData}
        triggerToast={triggerToast}
      />
    );
  }

  // Junk Mail Table List View
  return (
    <div className="junk-mail-container">
      <div className="junk-mail-card">
        {/* Top Control Bar */}
        {selectedRows.length > 0 ? (
          <>
            <div className="junk-mail-control-bar">
              <div style={{ fontSize: "14px", fontWeight: "600", color: "#101828" }}>
                Total records : {totalRecords}
              </div>
              <div className="junk-mail-actions">
                <div className="search-pill-wrapper">
                  <Search className="search-pill-icon" />
                  <input
                    type="text"
                    className="search-pill-input"
                    placeholder="Search tasks..."
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      setCurrentPage(1);
                    }}
                  />
                </div>
                <button className="action-pill-btn">
                  <Calendar size={16} color="#6929E2" />
                  <span>Date Range</span>
                </button>
                <button className="sort-pill-btn">
                  <SlidersHorizontal size={15} color="#667085" />
                  <span>Default <span style={{ color: "#98A2B3" }}>(Created)</span></span>
                  <ChevronDown size={14} color="#667085" />
                </button>
              </div>
            </div>

            {/* Bulk Selection Bar */}
            <div className="junk-mail-bulk-bar">
              <div className="bulk-select-info">
                <span>{selectedRows.length} tickets selected</span>
                <button
                  className="unselect-all-btn"
                  onClick={() => {
                    setSelectedRows([]);
                    setSelectAll(false);
                  }}
                >
                  <X size={14} />
                  <span>Unselect all</span>
                </button>
              </div>

              <div className="bulk-action-buttons">
                <button
                  className="btn-bulk-reject"
                  disabled={!!activeActionType}
                  onClick={() => triggerActionModal("Reject", true)}
                >
                  {activeActionType === "Reject" ? <Spinner animation="border" size="sm" /> : "Reject"}
                </button>
                <button
                  className="btn-bulk-create-ticket"
                  disabled={!!activeActionType}
                  onClick={() => triggerActionModal("Create_Ticket", true)}
                >
                  {activeActionType === "Create_Ticket" ? <Spinner animation="border" size="sm" /> : "Create Ticket"}
                </button>
                <button
                  className="btn-bulk-create-user-ticket"
                  disabled={!!activeActionType}
                  onClick={() => triggerActionModal("Create_User_and_Ticket", true)}
                >
                  {activeActionType === "Create_User_and_Ticket" ? <Spinner animation="border" size="sm" /> : "Create user & Ticket"}
                </button>
              </div>
            </div>
          </>
        ) : (
          <>
            {/* Header Title Section */}
            <div className="junk-mail-header">
              <h1 className="junk-mail-title">Junk Mail</h1>
              <p className="junk-mail-subtitle">Manage your register fields here very easily.</p>
            </div>

            {/* Action Controls & Filters */}
            <div className="junk-mail-control-bar">
              {/* Left Tabs */}
              <div className="junk-mail-tabs">
                <button
                  className={`junk-tab-btn ${activeTab === "junk" ? "active" : ""}`}
                  onClick={() => {
                    setActiveTab("junk");
                    setCurrentPage(1);
                  }}
                >
                  Junk Mails{activeTab === "junk" ? ` (${totalRecords})` : ""}
                </button>
                <button
                  className={`junk-tab-btn ${activeTab === "history" ? "active" : ""}`}
                  onClick={() => {
                    setActiveTab("history");
                    setCurrentPage(1);
                  }}
                >
                  History{activeTab === "history" ? ` (${totalRecords})` : ""}
                </button>
              </div>

              {/* Right Action Tools */}
              <div className="junk-mail-actions">
                <div className="search-pill-wrapper">
                  <Search className="search-pill-icon" />
                  <input
                    type="text"
                    className="search-pill-input"
                    placeholder="Search tasks..."
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      setCurrentPage(1);
                    }}
                  />
                </div>

                <button className="action-pill-btn">
                  <Calendar size={16} color="#6929E2" />
                  <span>Date Range</span>
                </button>

                <button className="sort-pill-btn">
                  <SlidersHorizontal size={15} color="#667085" />
                  <span>Default <span style={{ color: "#98A2B3" }}>(Created)</span></span>
                  <ChevronDown size={14} color="#667085" />
                </button>
              </div>
            </div>
          </>
        )}

        {/* Junk Mail Table */}
        <div className="junk-table-wrapper">
          <table className="junk-custom-table">
            <thead>
              {activeTab === "junk" ? (
                <tr>
                  <th style={{ width: "48px", textAlign: "center" }}>
                    <input
                      type="checkbox"
                      className="custom-checkbox-input"
                      checked={selectAll}
                      onChange={handleSelectAll}
                    />
                  </th>
                  <th>Subject</th>
                  <th>From Mail</th>
                  <th>Attachment</th>
                  <th>Date & Time</th>
                </tr>
              ) : (
                <tr>
                  <th>Subject</th>
                  <th>Date & Time</th>
                  <th>Status</th>
                  <th>Approver</th>
                  <th>Updated Date & Time</th>
                  <th>Remarks</th>
                </tr>
              )}
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={activeTab === "junk" ? 5 : 6} style={{ textAlign: "center", padding: "32px", color: "#667085" }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "8px" }}>
                      <Spinner animation="border" size="sm" style={{ color: "#6929E2" }} />
                      <span>Loading junk mail records...</span>
                    </div>
                  </td>
                </tr>
              ) : junkMails.length === 0 ? (
                <tr>
                  <td colSpan={activeTab === "junk" ? 5 : 6} style={{ textAlign: "center", padding: "32px", color: "#667085" }}>
                    No junk mail records found.
                  </td>
                </tr>
              ) : (
                junkMails.map((mail, index) => {
                  const mailId = mail.id || index;
                  const hasAttachments = Array.isArray(mail.attachments) ? mail.attachments.length > 0 : !!mail.attachments;

                  if (activeTab === "junk") {
                    return (
                      <tr
                        key={mailId}
                        style={{ cursor: "pointer" }}
                        onClick={() => handleViewMail(mailId)}
                      >
                        <td style={{ textAlign: "center" }} onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            className="custom-checkbox-input"
                            checked={selectedRows.includes(mailId)}
                            onChange={() => handleSelectRow(mailId)}
                          />
                        </td>
                        <td style={{ color: "#344054", fontWeight: "400" }}>
                          {mail.subject || "-"}
                        </td>
                        <td style={{ color: "#475467" }}>
                          {mail.from_email || "-"}
                        </td>
                        <td>
                          {hasAttachments ? (
                            <span className="attachment-pill yes">
                              <span className="icon-check-circle">
                                <Check size={12} strokeWidth={3} />
                              </span>
                              Yes
                            </span>
                          ) : (
                            <span className="attachment-pill no">
                              <span className="icon-cross-circle">
                                <X size={12} strokeWidth={3} />
                              </span>
                              No
                            </span>
                          )}
                        </td>
                        <td style={{ color: "#475467" }}>
                          {formatDateTime(mail.email_time || mail.created_at)}
                        </td>
                      </tr>
                    );
                  }

                  // History tab row rendering
                  const approverName = mail.approver_name || "-";
                  const approverPhoto = mail.approver_profile || null;
                  const statusValue = mail.status || mail.action || mail.approval_status || "Rejected";
                  const updatedDate = mail.updated_at || mail.action_at || mail.email_time || mail.created_at;
                  const remarksText = mail.remarks || mail.reason || "--:--";

                  return (
                    <tr
                      key={mailId}
                      style={{ cursor: "pointer" }}
                      onClick={() => handleViewMail(mailId)}
                    >
                      <td>
                        <div className="history-subject-cell">
                          <div className="history-avatar-circle">
                            {getInitials(mail.from_name, mail.from_email)}
                          </div>
                          <div className="history-subject-info">
                            <div className="history-subject-text">{mail.subject || "-"}</div>
                            <div className="history-email-text">&lt;{mail.from_email || "-"}&gt;</div>
                          </div>
                        </div>
                      </td>
                      <td style={{ color: "#475467", whiteSpace: "nowrap" }}>
                        {formatDateTime(mail.email_time || mail.created_at)}
                      </td>
                      <td>
                        {renderStatusBadge(statusValue)}
                      </td>
                      <td>
                        <div className="history-approver-cell">
                          {approverPhoto ? (
                            <img
                              src={approverPhoto}
                              alt={approverName}
                              className="approver-profile-img"
                              onError={(e) => {
                                e.target.style.display = "none";
                                if (e.target.nextSibling) {
                                  e.target.nextSibling.style.display = "flex";
                                }
                              }}
                            />
                          ) : null}
                          <div
                            className="approver-icon-circle"
                            style={{ display: approverPhoto ? "none" : "flex" }}
                          >
                            {approverName !== "-" ? (
                              getInitials(approverName)
                            ) : (
                              <User size={14} color="#64748B" />
                            )}
                          </div>
                          <span className="approver-name">{approverName}</span>
                        </div>
                      </td>
                      <td style={{ color: "#475467", whiteSpace: "nowrap" }}>
                        {formatDateTime(updatedDate)}
                      </td>
                      <td style={{ color: "#98A2B3" }}>
                        {remarksText}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Footer Pagination */}
        <div className="junk-pagination-footer">
          <div className="records-per-page-select">
            <span>Records per page :</span>
            <select
              className="page-select-dropdown"
              value={recordsPerPage}
              onChange={(e) => {
                setRecordsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
            </select>
          </div>

          <div className="pagination-numbers">
            <button
              className="page-nav-btn"
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
            >
              <ChevronLeft size={16} />
            </button>

            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => i + 1).map((pageNum) => (
              <button
                key={pageNum}
                className={`page-nav-btn ${currentPage === pageNum ? "active" : ""}`}
                onClick={() => setCurrentPage(pageNum)}
              >
                {pageNum}
              </button>
            ))}

            {totalPages > 5 && (
              <>
                <span style={{ color: "#667085", padding: "0 4px", fontSize: "14px" }}>...</span>
                <button
                  className={`page-nav-btn ${currentPage === totalPages ? "active" : ""}`}
                  onClick={() => setCurrentPage(totalPages)}
                >
                  {totalPages}
                </button>
              </>
            )}

            <button
              className="page-nav-btn"
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
            >
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      </div>

      {/* Action Comments / Remarks Modal */}
      <Modal
        show={showActionModal}
        onHide={() => !activeActionType && setShowActionModal(false)}
        centered
      >
        <Modal.Header closeButton={!activeActionType}>
          <Modal.Title style={{ fontSize: "16px", fontWeight: "700", color: "#101828" }}>
            {actionTarget?.action === "Reject"
              ? "Reject Junk Mail Request"
              : actionTarget?.action === "Create_Ticket"
              ? "Create Ticket"
              : "Create User & Ticket"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ marginBottom: "12px", fontSize: "14px", color: "#475467" }}>
            {actionTarget?.action === "Reject"
              ? "Please enter comments / reason for rejection:"
              : "Add any comments / remarks for this action (optional):"}
          </div>
          <textarea
            className="form-control"
            rows={4}
            placeholder="Type comments / reason here..."
            value={actionReason}
            onChange={(e) => setActionReason(e.target.value)}
            style={{ borderRadius: "8px", borderColor: "#D0D5DD", fontSize: "14px" }}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="light"
            disabled={!!activeActionType}
            onClick={() => setShowActionModal(false)}
            style={{ borderRadius: "20px", fontWeight: "600" }}
          >
            Cancel
          </Button>
          <Button
            style={{
              backgroundColor:
                actionTarget?.action === "Reject"
                  ? "#FF4D4F"
                  : actionTarget?.action === "Create_Ticket"
                  ? "#2CC879"
                  : "#6929E2",
              borderColor: "transparent",
              borderRadius: "20px",
              fontWeight: "600",
            }}
            disabled={!!activeActionType}
            onClick={handleConfirmAction}
          >
            {activeActionType ? (
              <Spinner animation="border" size="sm" />
            ) : actionTarget?.action === "Reject" ? (
              "Confirm Reject"
            ) : (
              "Confirm Action"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Toast Notification Container */}
      <ToastContainer position="top-end">
        <Toast
          className="d-inline-block m-1"
          bg={snackbarStatus === true ? "success" : "danger"}
          show={showSnackbar}
          onClose={handleSnackbarClose}
          delay={3000}
          autohide
        >
          <Toast.Body style={{ color: "#ffffff" }}>
            {snackbarMessage}
          </Toast.Body>
        </Toast>
      </ToastContainer>
    </div>
  );
};

export default JunkMailList;
